// Armazón del formulario de inscripción rediseñado (v2).
// Valida cada paso con props_c.validarDatos; el último (Confirmación) valida las aceptaciones y
// envía directo con props_c.enviarInscripcion, sin el modal del formulario anterior. Los pasos se van migrando uno a uno;
// mientras tanto, los no rediseñados usan los componentes actuales.
// Escritorio: formulario + columna derecha fija (Emma y ayuda). Celular: banda de ayuda y sin Emma.
import React, { useState, useRef } from "react";
import CssBaseline from "@material-ui/core/CssBaseline";
import Button from "@material-ui/core/Button";
import Hidden from "@material-ui/core/Hidden";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import PasoMisDatos from "./PasoMisDatos";
import PasoMiContacto from "./PasoMiContacto";
import PasoMiCarrera from "./PasoMiCarrera";
import emma_logo from "../../../global/imagenes/logo_agrupado_sinfondo.png";
import emma_inscripcion from "../../../global/imagenes/emma_inscripcion_inicio.png";
import { useEstilosInscripcion } from "./estilosInscripcion";
import { PieInscripcion } from "./componentesInscripcion";
import PantallaEnviada from "./PantallaEnviada";
import PasoCaracterizacion from "./PasoCaracterizacion";
import PasoConfirmacion from "./PasoConfirmacion";
import EnviandoInscripcion from "./EnviandoInscripcion";
import { PASOS_CARACTERIZACION, pasoCaracterizacion } from "./caracterizacion";
import { PASO_CONFIRMACION } from "./confirmacion";

const PASOS_BASE = ["Mis datos", "Mi contacto", "Mi carrera"];

// El último paso siempre es Confirmación; entre "Mi carrera" y él van los de caracterización (si llegaron preguntas)
function contenidoPaso(paso, ultimoPaso, props_c, setBotonSiguiente, irAPaso) {
  if (paso === ultimoPaso) return <PasoConfirmacion props_c={props_c} irAPaso={irAPaso} />;
  switch (paso) {
    case 0:
      return <PasoMisDatos props_c={props_c} setBotonSiguiente={setBotonSiguiente} />;
    case 1:
      return <PasoMiContacto props_c={props_c} />;
    case 2:
      return <PasoMiCarrera props_c={props_c} />;
    case 3:
    case 4:
      return <PasoCaracterizacion props_c={props_c} paso={pasoCaracterizacion(paso)} />;
    default:
      throw new Error("Paso desconocido");
  }
}

export default function InscripcionFormV2({ props_c }) {
  const classes = useEstilosInscripcion();
  const [pasoActivo, setPasoActivo] = useState(0);
  const [botonSiguiente, setBotonSiguiente] = useState(false);
  const { guardado, enviando, mensaje_final, mensajeAlerta, tipo_inscripcion_preg, preguntas_caracterizacion } = props_c;
  // Los pasos de caracterización solo se agregan si llegaron preguntas (si el servicio falla, quedan
  // los 3 de siempre más Confirmación)
  const PASOS = [
    ...PASOS_BASE,
    ...(preguntas_caracterizacion && preguntas_caracterizacion.length ? PASOS_CARACTERIZACION.map((p) => p.nombre) : []),
    PASO_CONFIRMACION,
  ];
  const ultimoPaso = PASOS.length - 1;
  const inicio = useRef(null);

  // Lleva la vista al inicio del formulario cuando ya se pintó el paso nuevo (sirve aunque el scroll sea de un contenedor)
  const irArriba = () => {
    setTimeout(() => {
      if (inicio.current) inicio.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const marcarError = (titulo) => {
    props_c.actualizarMensaje({ titulo, tipo: "info", mostrar: true, tiempo: 6000 });
    // Lleva la vista al campo marcado en rojo (se pinta en el siguiente render)
    setTimeout(() => {
      const campo = document.querySelector('[data-error="true"]');
      if (campo) campo.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const siguiente = () => {
    // Confirmación: valida las casillas y envía directo (sin el modal del formulario anterior)
    if (pasoActivo === ultimoPaso) {
      const titulo = props_c.validarConfirmacion();
      if (titulo) marcarError(titulo);
      else props_c.enviarInscripcion();
      return;
    }
    const titulo = props_c.validarDatos(pasoActivo);
    if (titulo) marcarError(titulo);
    else {
      setPasoActivo(pasoActivo + 1);
      irArriba();
    }
  };

  const irAPaso = (paso) => {
    setPasoActivo(paso);
    irArriba();
  };

  const anterior = () => irAPaso(pasoActivo - 1);

  if (guardado) {
    return (
      <PantallaEnviada
        mensajeFinal={mensaje_final}
        primerNombre={props_c.primer_nombre}
        correo={props_c.correo}
        celular={props_c.celular1}
        tipo={tipo_inscripcion_preg && tipo_inscripcion_preg.label}
        linkPolitica={props_c.link_politica}
      />
    );
  }

  const columnas = { gridTemplateColumns: `repeat(${PASOS.length}, minmax(0, 1fr))` };

  return (
    <div className={classes.raiz}>
      <CssBaseline />
      <main className={classes.pagina}>
        <div className={classes.distribucion}>
          <div className={classes.contenedor} ref={inicio}>
            <div className={classes.cabecera}>
              <div className={classes.marca}>
                <img src={emma_logo} alt="Logo Emma" className={classes.logoCabecera} />
                <span className={classes.separador} />
                <div>
                  <p className={classes.titulo}>Inscripciones Unicosta</p>
                  {tipo_inscripcion_preg && tipo_inscripcion_preg.label && (
                    <p className={classes.subtitulo}>{tipo_inscripcion_preg.label}</p>
                  )}
                </div>
              </div>
            </div>

            <Hidden mdUp>
              <div className={classes.banda}>
                <HelpOutlineIcon style={{ fontSize: 18 }} />
                <span>{mensajeAlerta}</span>
              </div>
            </Hidden>

            <div className={classes.progreso} style={columnas}>
              {PASOS.map((paso, i) => (
                <div key={paso} className={`${classes.tramo} ${i <= pasoActivo ? classes.tramoActivo : ""}`} />
              ))}
            </div>
            <div className={classes.etiquetas} style={columnas}>
              {PASOS.map((paso, i) => (
                <span key={paso} className={`${classes.etiqueta} ${i === pasoActivo ? classes.etiquetaActiva : ""}`}>
                  {paso}
                </span>
              ))}
            </div>

            {contenidoPaso(pasoActivo, ultimoPaso, props_c, setBotonSiguiente, irAPaso)}

            <div className={classes.acciones}>
              {pasoActivo !== 0 ? (
                <Button className={classes.botonSecundario} startIcon={<ArrowBackIcon />} onClick={anterior} disabled={enviando}>
                  Anterior
                </Button>
              ) : <span />}
              <Button className={classes.botonPrincipal} endIcon={<ArrowForwardIcon />} onClick={siguiente} disabled={botonSiguiente || enviando}>
                {pasoActivo === ultimoPaso ? "Enviar inscripción" : "Continuar"}
              </Button>
            </div>
          </div>

          <Hidden smDown>
            <aside className={classes.columnaLateral}>
              <div className={classes.tarjetaLateral}>
                <img src={emma_inscripcion} alt="Emma" className={classes.emma} />
              </div>
              <div className={classes.tarjetaLateral}>
                <p className={classes.ayudaTitulo}>
                  <HelpOutlineIcon style={{ fontSize: 20 }} className={classes.seccionIcono} />
                  ¿Necesitas ayuda?
                </p>
                <p className={classes.ayudaTexto}>{mensajeAlerta}</p>
              </div>
            </aside>
          </Hidden>
        </div>
      </main>
      <PieInscripcion linkPolitica={props_c.link_politica} />
      {enviando && <EnviandoInscripcion />}
    </div>
  );
}
